import { Component } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Observable, of } from 'rxjs';

import { Utils } from '@shared/fork-common-controls/utils.service';
import {
  CreateMortgageLoanApplicationRequest,
  DashboardData,
  LoanFacts,
  PersonalLoanReapplyStatusResponse,
  TouchpointRequest,
  TouchpointResponse
} from '@shared/models';
import {
  BorrowerPersonalLoanApplicationService,
  BorrowerService,
  DashboardService,
  FeatureToggleService,
  MixpanelService,
  SessionService
} from '@shared/services';
import { PersonalLoansSessionDataModel } from '@shared/services/session';
// import { SvgIconUtils } from '@common-library';
import { LoanStateDirectorService as PLLoanStateDirector } from '@personal-loans/shared/services';
import { QueryParamService } from '../services/session/shared/session/query-param-service/query-param.service';

import LoanPurpose = CreateMortgageLoanApplicationRequest.LoanPurposeEnum;
import ReapplyStatusEnum = PersonalLoanReapplyStatusResponse.StatusEnum;
import TouchpointTypeEnum = TouchpointRequest.TouchpointTypeEnum;
import CreationEntryPointEnum = TouchpointRequest.CreationEntryPointEnum;
import { SvgIconUtils } from '@app/common-library/svg-icon/svg-icon-utils';

enum BannerContextEnum {
  ConsumerPortal = <any>'ConsumerPortal',
  Login = <any>'Login',
  Dashboard = <any>'Dashboard',
  ForgetPassword = <any>'ForgetPassword',
  UpdatePassword = <any>'UpdatePassword',
  Welcome = <any>'Welcome',
  PersonalLoan = <any>'PersonalLoan',
  Default = <any>'Default'
}

@Component({
  selector: 'ld-banner',
  templateUrl: './banner.component.html',
  styleUrls: [ './banner.style.scss' ]
})
export class BannerComponent {
  public bannerContextEnum = BannerContextEnum;
  public shouldShowBanner: boolean = true;

  private bannerContext: BannerContextEnum = null;
  private dashboardData: DashboardData;
  private disablePersonalLoanApplyLink: boolean = false;
  private hiddenSmallDownClassIsActive: boolean = true;
  private isPersonalLoanDashboardEnabled: boolean = false;
  private oneBorrowerPerLoan: boolean;
  private showApplyNowMenu: boolean = false;
  private plData: PersonalLoansSessionDataModel;

  constructor(
    private borrowerPersonalLoanApplicationService: BorrowerPersonalLoanApplicationService,
    private borrowerService: BorrowerService,
    private dashboardService: DashboardService,
    private features: FeatureToggleService,
    private mixpanelService: MixpanelService,
    private plLoanStateDirector: PLLoanStateDirector,
    private queryParamService: QueryParamService,
    private router: Router,
    private sessionService: SessionService) {

    this.plData = this.sessionService.personalLoanSession.data;

    features.isPersonalLoansDashboardEnabled.subscribe(
      (enabled) => this.isPersonalLoanDashboardEnabled = enabled
    );

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const navEndEvent: NavigationEnd = <NavigationEnd>event;

        this.setBannerContext();
        this.hiddenSmallDownClassIsActive = this.shouldHideBannerWhenSmall();

        if (navEndEvent.url.includes('dashboard')) {
          this.dashboardService.getLoanFacts(false)
            .subscribe((data: DashboardData) => {
              this.dashboardData = data;
              this.oneBorrowerPerLoan = data.loans.every(loan => {
                return loan.borrowers.length === 1;
              });

              // We're retrieving the primary borrower ID so we can retrieve personal info to populate register form.
              const latestCreatedLoan = this.getLatestCreatedLoan(data);

              // LDTSULA-4034: When there are multiple borrowers on a loan show empty registration form

              // LDTSULA-4458: Due to the new dashboard worflow, if there are multiple borrowers,
              // you have to identify which borrower you are on login.
              // this.sessionService.mortgageSession.data.borrowerData.primaryBorrowerId =
              // (this.oneBorrowerPerLoan ? latestCreatedLoan.borrowers[0].borrowerID : null);
            });
          this.borrowerPersonalLoanApplicationService.getPersonalLoanReapplyStatus()
            .subscribe((data: PersonalLoanReapplyStatusResponse) => {
              this.disablePersonalLoanApplyLink = data.status === ReapplyStatusEnum.DoNotAllow;
            });
        }
      }
    });
  }

  get sessionLoanPurpose(): LoanPurpose {
    return this.sessionService.mortgageSession.data.loanPurpose;
  }

  get isPurchase(): boolean {
    return this.sessionLoanPurpose === LoanPurpose.Purchase;
  }

  get hasPurpose(): boolean {
    return !!this.sessionLoanPurpose;
  }

  get hasLeadNumber(): boolean {
    return !!this.leadNumber;
  }

  get leadNumber(): string {
    return this.sessionService.mortgageSession.data.leadNumber;
  }

  get loanPurpose(): string {
    let purpose = '';
    if (this.isPurchase) {
      purpose = 'Purchase';
    } else {
      purpose = 'Refinance';
    }
    return purpose;
  }

  get pathToIconId(): string {
    let svgPath = '';
    if (this.isPurchase) {
      svgPath = 'icon-keys';
    } else {
      svgPath = 'icon-refi';
    }
    return SvgIconUtils.appendDefaultToIconPath(svgPath);
  }

  private setBannerContext() {
    // reset the shouldShowBanner value to true
    this.shouldShowBanner = true;
    if (this.router.url.indexOf('login') > -1 || this.router.url.indexOf('sign-in') > -1) {
      this.bannerContext = BannerContextEnum.Login;
      this.shouldShowBanner = false;
    } else if (this.router.url.indexOf('dashboard') > -1) {
     this.bannerContext = BannerContextEnum.Dashboard;
    } else if (this.router.url.indexOf('update-password') > -1) {
     this.bannerContext = BannerContextEnum.UpdatePassword;
    } else if (this.router.url.indexOf('forgot-password') > -1) {
      this.bannerContext = BannerContextEnum.ForgetPassword;
      this.shouldShowBanner = false;
    } else if (this.router.url.indexOf('welcome') > -1) {
      this.bannerContext = BannerContextEnum.Welcome;
      this.shouldShowBanner = false;
    } else if (
      this.router.url.indexOf('personal-loans/todos') > -1 ||
      this.router.url.indexOf('personal-loans/messages') > -1 ||
      this.router.url.indexOf('personal-loans/documents') > -1 ||
      this.router.url.indexOf('personal-loans/contact') > -1
    ) {
      this.bannerContext = BannerContextEnum.ConsumerPortal;
    } else if (this.router.url.indexOf('personal-loans') > -1) {
      this.bannerContext = BannerContextEnum.PersonalLoan;
    } else {
      this.bannerContext = BannerContextEnum.Default;
    }
  }

  private shouldHideBannerWhenSmall(): boolean {
    const bannerContextIsNotConsumerPortal: boolean = (this.bannerContext !== BannerContextEnum.ConsumerPortal);
    const bannerContextIsNotDashboard: boolean = (this.bannerContext !== BannerContextEnum.Dashboard);

    return (bannerContextIsNotConsumerPortal && bannerContextIsNotDashboard);
  }

  private hideApplyNow(): void {
    this.showApplyNowMenu = false;
  }

  private toggleApplyNow(e: MouseEvent): void {
    if (this.showApplyNowMenu) {
      this.hideApplyNow();
      return;
    }

    // If the Apply Now menu is not showing, show it and add a click handler to hide it on the next click.
    const hideOnClick = () => {
      this.hideApplyNow();
      window.removeEventListener('click', hideOnClick);
    };

    window.addEventListener('click', hideOnClick);

    this.showApplyNowMenu = true;
    e.stopPropagation();
  }

  private startNewPersonalLoan(): void {
    document.title = 'Personal Loans';

    // Log analytics
    const analyticsObj = {
      'Definition': 'Personal loan product selected from Apply Now dropdown',
      'Product': 'P',
      'User': 'B',
      'Experience': 'O',
    };

    this.mixpanelService.trackSuper({ 'Lead ID': '' });
    this.mixpanelService.peopleSet({ 'Lead ID': '', 'TCPA Origin': ''});
    this.mixpanelService.track('Loan Application Started PBO', analyticsObj);

    this.borrowerService.getUnifiedBorrower().subscribe(borrower => {
      const request = {
        interactionGuid: this.plData.interactionGuid || null,
        touchpointType: TouchpointTypeEnum.WebReapply,
        borrowerTouchpoint: {
          emailAddress: borrower.email,
          phoneNumber: Utils.formatPhoneNumberForBackEnd(borrower.phoneNumber),
          zipCode: borrower.zipCode,
          state: borrower.state,
          city: borrower.city,
          addressLine1: borrower.address,
          addressLine2: borrower.addressUnit,
          suffix: borrower.suffix,
          firstName: borrower.firstName,
          lastName: borrower.lastName,
          leadSource: this.queryParamService.leadsource.getValue(),
          promoCode: this.plData.promoCode,
          interactionGuid: this.plData.interactionGuid || null,
          touchpointType: TouchpointTypeEnum.WebReapply,
          creationEntryPoint: CreationEntryPointEnum.Web
        }
      };

      this.createTouchpoint(request).subscribe(() => {
        this.plLoanStateDirector.directToReapply(
          undefined,
          undefined,
          this.sessionService.mortgageSession.data.borrowerData.primaryBorrowerData.borrowerGuid);
        });
    });
  }

  private getLatestCreatedLoan(dashboardFacts: DashboardData): LoanFacts {
    const latestCreatedLoan = dashboardFacts.loans.sort((a: LoanFacts, b: LoanFacts) => {

      const dateA = new Date(a.createdDate as any);
      const dateB = new Date(b.createdDate as any);

      return dateA.getTime() - dateB.getTime();
    }).pop();

    return latestCreatedLoan;
  }

  private createTouchpoint(request: TouchpointRequest): Observable<TouchpointResponse> {
    return this.borrowerPersonalLoanApplicationService.createTouchpoint(request)
      .map((response: TouchpointResponse) => {
        this.plData.interactionGuid = response.interactionGuid;
        return response;
      })
      .catch(error => {
        console.error(error);
        return of(null);
      });
  }
}
